import React, { useCallback, useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { DesktopTableLoader, MobileCardsLoader } from "components/DataTableLoader";
import { ViewIconButton, DeleteIconButton, DownloadIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";
import { getAuthCookie } from "../../utils/authCookies";

type DesignRow = {
  id: string;
  /** Id used in /orderDetails/:id or /installationWorkOrder/:id — may match `id` or come from API-specific fields */
  viewTargetId: string;
  source_type?: string;
  submitted_date?: string;
  order_number?: string;
  name?: string;
  email?: string;
  channel?: string;
  view_url?: string;
  download_url?: string;
  [key: string]: unknown;
};

function idFallback(raw: Record<string, unknown>): string {
  return String(raw._id ?? raw.id ?? "");
}

/** Prefer explicit relation ids from API when present; otherwise design row `id`. */
function resolveViewTargetId(raw: Record<string, unknown>, sourceType: string): string {
  const st = sourceType.trim().toLowerCase();
  if (st === "order") {
    return String(raw.order_id ?? raw.orderId ?? idFallback(raw));
  }
  if (st === "installation_order") {
    return String(
      raw.installation_order_id ??
        raw.installation_work_order_id ??
        raw.installation_id ??
        raw.work_order_id ??
        idFallback(raw)
    );
  }
  return idFallback(raw);
}

/** In-app route for View / order link based on `source_type`. */
function getDesignViewPath(baseRoute: string, row: DesignRow): string | null {
  const st = (row.source_type || "").trim().toLowerCase();
  const target = (row.viewTargetId || row.id).trim();
  if (!target) return null;
  if (st === "order") return `${baseRoute}/orderDetails/${encodeURIComponent(target)}`;
  if (st === "installation_order") return `${baseRoute}/jobs/installationWorkOrder/${encodeURIComponent(target)}`;
  return null;
}

function normalizeDesignRow(raw: Record<string, unknown>): DesignRow {
  const sourceType = String(raw.source_type ?? "");
  const id = idFallback(raw);
  const viewTargetId = resolveViewTargetId(raw, sourceType);
  const nameRaw = raw.name;
  const name =
    typeof nameRaw === "string" && nameRaw.trim()
      ? nameRaw
      : [raw.first_name, raw.last_name]
          .filter((x) => typeof x === "string" && x.trim())
          .join(" ")
          .trim() || String(raw.customer_name ?? raw.full_name ?? "—");
  return {
    ...raw,
    id,
    source_type: sourceType,
    viewTargetId,
    submitted_date: raw.submitted_date != null ? String(raw.submitted_date) : "",
    order_number: String(raw.order_number ?? raw.order_no ?? raw.orderNumber ?? "—"),
    name,
    email: String(raw.email ?? "—"),
    channel: String(raw.channel ?? raw.source ?? "—"),
    view_url: typeof raw.view_url === "string" ? raw.view_url : typeof raw.preview_url === "string" ? raw.preview_url : undefined,
    download_url:
      typeof raw.download_url === "string"
        ? raw.download_url
        : typeof raw.file_url === "string"
          ? raw.file_url
          : undefined,
  };
}

function formatUsDate(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

function ChannelBadge({ channel }: { channel: string }) {
  const c = (channel || "").trim();
  const lower = c.toLowerCase();
  const isStore = lower.includes("store");
  const label = c && c !== "—" ? c : "—";
  if (label === "—") return <span className="text-muted">—</span>;
  return (
    <span
      className="badge rounded-pill px-2 py-1 fw-semibold"
      style={
        isStore
          ? { background: "#F3E8FF", color: "#7C3AED", fontSize: "12px" }
          : { background: "#EFF6FF", color: "#2563EB", fontSize: "12px" }
      }
    >
      {label}
    </span>
  );
}

const columnHelper = createColumnHelper<DesignRow>();

const Designs = () => {
  const [rows, setRows] = useState<DesignRow[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const location = useLocation();
  const navigate = useNavigate();
  const isSignageTeammember = location.pathname.includes("/signageteammember");
  const baseRoute = isSignageTeammember ? "/signageteammember" : "/signage";

  const [parentSignageId, setParentSignageId] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const topRef = useRef<HTMLDivElement>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetLabel, setDeleteTargetLabel] = useState("");

  const signageId = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";

  const fetchParentHierarchy = useCallback(async () => {
    if (!isSignageTeammember || !token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/parent-hierarchy`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const signageFirm = data.parent_chain?.find((parent: { license_type?: string }) => parent.license_type === "SIGNAGE_FIRM");
      if (signageFirm) {
        setParentSignageId(signageFirm.id || signageFirm._id);
      }
    } catch {
      /* ignore */
    }
  }, [isSignageTeammember, token]);

  useEffect(() => {
    if (isSignageTeammember && token) fetchParentHierarchy();
  }, [isSignageTeammember, token, fetchParentHierarchy]);

  useEffect(() => {
    const load = async () => {
      if (!token || !signageId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const url = `${process.env.REACT_APP_API_URL}/realesy-studio/customized/designs/signage/${encodeURIComponent(signageId)}/datatable`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            date_range: { type: "", from: "", to: "" },
            filters: {},
            sort: { field: "submitted_date", order: "desc" },
          }),
        });
        if (!response.ok) {
          const t = await response.text().catch(() => "");
          throw new Error(t || `Failed to load designs (${response.status})`);
        }
        const result = await response.json();
        const rawList = Array.isArray(result?.data) ? result.data : Array.isArray(result?.results) ? result.results : [];
        const total = typeof result?.total === "number" ? result.total : typeof result?.count === "number" ? result.count : rawList.length;
        setRowCount(total);
        setRows(rawList.map((r: Record<string, unknown>) => normalizeDesignRow(r)));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load designs");
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, signageId, globalFilter, pagination.pageIndex, pagination.pageSize]);

  const handleView = useCallback(
    (row: DesignRow) => {
      const path = getDesignViewPath(baseRoute, row);
      if (path) {
        navigate(path);
        return;
      }
      if (row.view_url) {
        window.open(row.view_url, "_blank", "noopener,noreferrer");
        return;
      }
      showError("Unable to open this design (unknown source type).");
    },
    [baseRoute, navigate]
  );

  const handleDownload = useCallback((row: DesignRow) => {
    const u = row.download_url;
    if (u) {
      window.open(u, "_blank", "noopener,noreferrer");
      return;
    }
    showError("Download is not available for this design.");
  }, []);

  const confirmDelete = async () => {
    if (!deleteTargetId || !token || !signageId) return;
    setDeleting(deleteTargetId);
    try {
      const delUrl = `${process.env.REACT_APP_API_URL}/realesy-studio/customized/designs/signage/${encodeURIComponent(signageId)}/${encodeURIComponent(deleteTargetId)}`;
      const response = await fetch(delUrl, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = (body as { error?: string; message?: string })?.error || (body as { message?: string }).message || "Failed to delete";
        throw new Error(msg);
      }
      showSuccess("Design removed.");
      setRows((prev) => prev.filter((r) => r.id !== deleteTargetId));
      setRowCount((c) => Math.max(0, c - 1));
    } catch (err) {
      showError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeleting(null);
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetLabel("");
    }
  };

  const openDeleteFor = (row: DesignRow) => {
    setDeleteTargetId(row.id);
    setDeleteTargetLabel(row.order_number || row.name || "Design");
    setOpenDelete(true);
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No</div>,
        cell: (info) => (
          <div className="text-sm S-No">{pagination.pageIndex * pagination.pageSize + info.row.index + 1}</div>
        ),
      }),
      columnHelper.accessor((row) => row.submitted_date, {
        id: "submitted_date",
        header: () => <div className="th-text">Submitted Date</div>,
        cell: (info) => <div className="text-sm">{formatUsDate(info.getValue())}</div>,
      }),
      columnHelper.accessor((row) => row.order_number, {
        id: "order_number",
        header: () => <div className="th-text">Order Number</div>,
        cell: (info) => {
          const v = info.getValue();
          const row = info.row.original;
          if (!v || v === "—") return <span className="text-muted">—</span>;
          const detailPath = getDesignViewPath(baseRoute, row);
          if (detailPath) {
            return (
              <Link to={detailPath} className="text-primary fw-semibold text-decoration-none">
                {v}
              </Link>
            );
          }
          return (
            <Link
              to={`${baseRoute}/orders?search=${encodeURIComponent(v)}`}
              className="text-primary fw-semibold text-decoration-none"
            >
              {v}
            </Link>
          );
        },
      }),
      columnHelper.accessor((row) => row.name, {
        id: "name",
        header: () => <div className="th-text">Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      }),
      columnHelper.accessor((row) => row.email, {
        id: "email",
        header: () => <div className="th-text">Email</div>,
        cell: (info) => <div className="text-sm text-break">{info.getValue()}</div>,
      }),
      columnHelper.accessor((row) => row.channel, {
        id: "channel",
        header: () => <div className="th-text">Channel</div>,
        cell: (info) => <ChannelBadge channel={info.getValue() || ""} />,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <ViewIconButton title="View" className="icon-square" onClick={() => handleView(row)} />
              {/* <DownloadIconButton
                title="Download"
                className="icon-square designs-download-btn"
                onClick={() => handleDownload(row)}
              />
              <DeleteIconButton
                title="Delete"
                className="icon-square danger"
                onClick={() => openDeleteFor(row)}
                disabled={deleting === row.id}
              /> */}
            </div>
          );
        },
      }),
    ],
    [pagination.pageIndex, pagination.pageSize, baseRoute, deleting, handleView, handleDownload]
  );

  const table = useReactTable({
    data: rows,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: { pagination, globalFilter },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [pagination.pageIndex]);

  return (
    <>
      <style>{`
        .designs-download-btn.sqbtn {
          background: #ecfdf5 !important;
          color: #059669 !important;
          border: 1px solid #a7f3d0 !important;
        }
        .designs-download-btn.sqbtn:hover {
          background: #d1fae5 !important;
          color: #047857 !important;
        }
      `}</style>
      <div className="content" ref={topRef}>
        <div className="po-header mb-4 shadow-border-datatables">
          <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
            <div className="po-title-wrap me-2 d-flex align-items-center gap-2">
              <h4 className="mb-0 po-title">Designs</h4>
            </div>
            <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
              <form
                className="input-group po-search"
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  setGlobalFilter(searchInput);
                }}
              >
                <span className="input-group-text">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="search"
                  id="signage-designs-search"
                  name="signage_designs_filter"
                  className="form-control"
                  placeholder="Search by Order Number, Name, Email..."
                  value={searchInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                      setGlobalFilter(searchInput);
                    }
                  }}
                  autoComplete="new-password"
                  data-lpignore="true"
                />
              </form>
            </div>
          </div>
        </div>

        {error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : null}

        <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
          <div className="rdt-desktop shadow-sm">
            <div className="production-scroll" style={{ "--rdt-min": "1100px" } as React.CSSProperties}>
              {loading ? (
                <table className="table production-table align-middle mb-0 table-1">
                  <DesktopTableLoader table={table} message="Loading designs…" />
                </table>
              ) : (
                <table className="table production-table align-middle mb-0 table-1">
                  <DesktopTableParts table={table} />
                </table>
              )}
            </div>
          </div>
          <div className="rdt-mobile">
            {loading ? (
              <MobileCardsLoader message="Loading designs…" />
            ) : (
              <MobileCardsOnly table={table} idColId="order_number" titleColId="name" actionColId="actions" />
            )}
          </div>
        </div>

        {!loading && table.getRowModel().rows.length === 0 ? (
          <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
            No records found
          </div>
        ) : !loading && table.getRowModel().rows.length > 0 ? (
          <div className="mt-4 mb-4">
            <TablePaginationControls table={table} />
          </div>
        ) : null}
      </div>

      <DeleteConfirmModal
        open={openDelete}
        dynamicName={deleteTargetLabel || "Design"}
        isDeleting={Boolean(deleting)}
        onCancel={() => {
          setOpenDelete(false);
          setDeleteTargetId(null);
          setDeleteTargetLabel("");
        }}
        onDelete={confirmDelete}
      />
    </>
  );
};

export default Designs;
